import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useSiteContext } from "../contexts/SiteContext";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabaseClient";
import { saveChatHistory, loadChatHistory, startNewChat } from "../services/auditStorageService";
import { saveArtifact, type ArtifactType } from "../services/artifactService";
import { useMCPContext, type PrioritizedIssue } from "../hooks/useMCPContext";
import QuickFixList from "../quick-fixes/QuickFixList";
import PostFixActions from "../quick-fixes/PostFixActions";
import RubyWelcomeMessage from "../quick-fixes/RubyWelcomeMessage";
import { getCmsImplementationGuide, IssueFixType } from "../quick-fixes/cmsGuides";
import { resolveFixType, resolveArtifactFile, downloadArtifact } from "../quick-fixes/fixArtifacts";
import { canGenerateFix } from "@/components/subscriptions/subscriptionService";
import {
  Send,
  Sparkles,
  Paperclip,
  Loader2,
  User,
  RotateCcw,
  History,
  Copy,
  Check,
  Save,
  Download,
  Code,
  ChevronRight,
  Zap,
} from "lucide-react";

interface ChatMessage {
  id: string;
  role: "assistant" | "user";
  content?: string;
  quickFixList?: boolean;
  quickFixButton?: boolean;
  issues?: PrioritizedIssue[];
  codeBlock?: {
    code: string;
    language: string;
    title: string;
    artifactType: string;
    filename?: string;
    mime?: string;
    emphasize?: boolean;
  };
  postFixActions?: boolean;
  activeIssueId?: string;
  retryAction?: boolean;
  cmsOptions?: boolean;
  cmsGuide?: boolean;
  isWelcome?: boolean;
}

interface RubyQuickFixesWidgetProps {
  cardId: string;
  activeTab?: string;
  onIssueFixed?: () => void;
}

const CMS_OPTIONS = ["WordPress", "Shopify", "Wix", "Squarespace", "Webflow", "Custom HTML", "Other"];

// Ruby keeps the brand gradient identity across all cards; accent tints derive from --accent.
const BRAND_GRADIENT = "linear-gradient(98deg,#8B5CF6 0%,#B16CEA 46%,#FF7A18 100%)";
const ACCENT = "var(--accent, #8B5CF6)";

function renderContent(content: string) {
  return content.split("\n").map((line, i) => (
    <React.Fragment key={i}>
      {line.includes("**") ? (
        <div className={i > 0 ? "mt-1" : ""}>
          {line.split("**").map((part, j) =>
            j % 2 === 1 ? (
              <span key={j} className="font-semibold" style={{ color: "#E8E4FF" }}>
                {part}
              </span>
            ) : (
              <span key={j}>{part}</span>
            )
          )}
        </div>
      ) : (
        <div className={i > 0 && line ? "mt-1" : ""}>{line}</div>
      )}
    </React.Fragment>
  ));
}

const RubyQuickFixesWidget: React.FC<RubyQuickFixesWidgetProps> = ({
  cardId,
  activeTab = "overview",
  onIssueFixed,
}) => {
  const { activeSite, selectedReportId } = useSiteContext();
  const { user } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [completedIds, setCompletedIds] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [hasHistory, setHasHistory] = useState(false);
  const [copiedCodeId, setCopiedCodeId] = useState<string | null>(null);
  const [activeIssue, setActiveIssue] = useState<PrioritizedIssue | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const chatSessionRef = useRef<string>("");

  const domain = activeSite?.domain || "";

  const {
    isLoading: mcpLoading,
    error: mcpError,
    overview,
    priorityQueue,
    allIssues,
    markIssueFixed,
    getIssuesByCard,
    getIssueById,
    generateFixPrompt,
    buildContextForClaude,
  } = useMCPContext({ domain, reportId: selectedReportId });

  const historyReportId = selectedReportId || overview?.reportId;
  const historyTabId = JSON.stringify([activeTab, domain, historyReportId]);
  const historyContextRef = useRef(historyTabId);
  historyContextRef.current = historyTabId;

  const cardPendingIssues = useMemo(() => getIssuesByCard(cardId).filter((i) => i.status === "pending"), [cardId, getIssuesByCard]);

  const scrollToBottom = () => {
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
  };

  const initWelcome = useCallback(() => {
    setActiveIssue(null);
    setCompletedIds([]);
    setIsLoading(false);
    setMessages([
      {
        id: `welcome-${Date.now()}`,
        role: "assistant",
        isWelcome: true,
      },
    ]);
  }, []);

  useEffect(() => {
    const sessionKey = historyTabId;
    if (!domain) {
      chatSessionRef.current = "";
      initWelcome();
      return;
    }
    if (mcpLoading) return;
    if (chatSessionRef.current === sessionKey) return;
    chatSessionRef.current = sessionKey;
    initWelcome();
  }, [cardId, domain, historyTabId, mcpLoading, initWelcome]);

  useEffect(() => {
    setCompletedIds((prev) => prev.filter((id) => cardPendingIssues.some((i) => i.id === id)));
  }, [cardPendingIssues]);

  const handleQuickFixesClick = () => {
    const userMsg: ChatMessage = {
      id: `user-qf-${Date.now()}`,
      role: "user",
      content: "⚡ Quick Fixes — start fixing",
    };
    const listMsg: ChatMessage = {
      id: `qf-list-${Date.now()}`,
      role: "assistant",
      content: "",
      quickFixList: true,
      issues: cardPendingIssues,
    };
    setMessages((prev) => [...prev, userMsg, listMsg]);
    scrollToBottom();
  };

  const generateFix = async (
    issue: PrioritizedIssue,
    extraContext?: string,
    options?: { isRetry?: boolean }
  ) => {
    const requestContext = historyTabId;
    const isCurrentContext = () => historyContextRef.current === requestContext;
    setActiveIssue(issue);
    if (!options?.isRetry) {
      setMessages((prev) => [
        ...prev,
        {
          id: `user-fix-${Date.now()}`,
          role: "user",
          content: `Generate fix: ${issue.title}`,
        },
      ]);
    }
    setIsLoading(true);
    scrollToBottom();

    // Gap 2: upgrade the MCP's coarse fixType into the richest card-specific type so both the
    // prompt and the downloadable artifact route correctly (disavow, local, csv, robots, etc.).
    const fixType = resolveFixType(issue);

    try {
      if (!options?.isRetry) {
        const allowed = await canGenerateFix();
        if (!isCurrentContext()) return;
        if (!allowed) {
          setMessages((prev) => [
            ...prev,
            {
              id: `no-units-${Date.now()}`,
              role: "assistant",
              content:
                "You're out of remediation units for this cycle. Top up with a booster pack on **Subscriptions**, or wait until your monthly allowance renews.",
              activeIssueId: issue.id,
            },
          ]);
          return;
        }
      }

      const contextParts: string[] = [];
      if (domain) {
        contextParts.push(`**Authority context:**\n${await buildContextForClaude()}`);
      }
      if (extraContext) {
        contextParts.push(`**User clarification:** ${extraContext}`);
      }

      const fixPrompt = await generateFixPrompt({ ...issue, fixType }, domain, contextParts.join("\n\n"));
      if (!isCurrentContext()) return;

      const { data, error } = await supabase.functions.invoke("ruby-ai-seo-analysis", {
        body: {
          websiteUrl: domain ? `https://${domain}/` : "",
          userId: user?.id,
          issueId: issue.id,
          userMessage: fixPrompt,
          region: "au",
          analysisType: issue.analysisType,
        },
      });

      if (!isCurrentContext()) return;
      if (error) throw error;
      if (data?.success === false) {
        throw new Error(data?.error || "Fix generation failed");
      }

      let code = "";
      let language = fixType === "schema" || fixType === "faq" ? "json" : "html";
      let analysisText = data?.analysis || "";

      if (analysisText.includes("synthesis failed") || analysisText.includes("analysis failed")) {
        if (fixType === "entity" && issue.steps?.length) {
          analysisText = issue.steps
            .map((s) => `**Step ${s.step}** (${s.tag}): ${s.instruction}`)
            .join("\n\n");
        } else {
          throw new Error("Ruby couldn't produce a fix for this issue");
        }
      } else {
        const codeMatch = analysisText.match(/```(\w+)?\n([\s\S]*?)```/);
        if (codeMatch) {
          language = codeMatch[1] || language;
          code = codeMatch[2].trim();
          analysisText = analysisText.replace(/```[\s\S]*?```/g, "").trim();
        } else {
          code = analysisText;
          language =
            fixType === "schema" || fixType === "faq"
              ? "json"
              : fixType === "meta" || fixType === "title" || fixType === "h1"
                ? "html"
                : "text";
        }
      }

      if (!code && !analysisText) {
        throw new Error("Ruby returned an empty response");
      }

      const artifactFile = code ? resolveArtifactFile(fixType, language, issue.title) : undefined;
      const codeMsg: ChatMessage = {
        id: `code-${Date.now()}`,
        role: "assistant",
        content: analysisText || `Here's your **${issue.title}** — ready to use:`,
        codeBlock: code
          ? {
              code,
              language,
              title: issue.title,
              artifactType: fixType,
              filename: artifactFile?.filename,
              mime: artifactFile?.mime,
              emphasize: artifactFile?.emphasize,
            }
          : undefined,
        postFixActions: true,
        activeIssueId: issue.id,
      };
      setMessages((prev) => [...prev, codeMsg]);
    } catch (err) {
      if (!isCurrentContext()) return;
      console.error("Error generating fix:", err);
      const reason = err instanceof Error ? err.message : "Something went wrong. Please try again.";
      setMessages((prev) => [
        ...prev,
        {
          id: `gen-error-${Date.now()}`,
          role: "assistant",
          content: `⚠️ **Couldn't generate your fix**\n\n${reason}\n\nTap **Try again** and I'll retry.`,
          retryAction: true,
          activeIssueId: issue.id,
        },
      ]);
    } finally {
      if (isCurrentContext()) { setIsLoading(false); scrollToBottom(); }
    }
  };

  const handleRetryGeneration = (issueId: string) => {
    const issue = getIssueById(issueId) ?? activeIssue;
    if (issue) generateFix(issue, undefined, { isRetry: true });
  };
  const handleIssueClick = async (issue: PrioritizedIssue) => {
    if (issue.needsClarification) {
      setMessages((prev) => [
        ...prev,
        { id: `user-${Date.now()}`, role: "user", content: issue.title },
        {
          id: `clarify-${Date.now()}`,
          role: "assistant",
          content: `Before I generate this fix, what's the main topic or angle you'd like to focus on for **${issue.title}**?`,
        },
      ]);
      setActiveIssue(issue);
      scrollToBottom();
      return;
    }
    await generateFix(issue);
  };

  const handleMarkedFixed = async (issueId: string) => {
    if (domain) {
      await markIssueFixed(issueId, cardId);
      onIssueFixed?.();
    }
    setCompletedIds((prev) => prev.includes(issueId) ? prev : [...prev, issueId]);
    const pending = cardPendingIssues.filter((i) => i.id !== issueId);
    setMessages((prev) => [
      ...prev,
      {
        id: `continue-${Date.now()}`,
        role: "assistant",
        content:
          pending.length > 0
            ? `✅ Fix marked complete! Continue with your remaining Quick Fixes (${pending.length} left):`
            : "🎉 All issues fixed! Great work improving your site.",
        quickFixList: pending.length > 0,
        issues: pending,
      },
    ]);
    scrollToBottom();
  };

  const handleNeedHelp = () => {
    setMessages((prev) => [
      ...prev,
      {
        id: `cms-${Date.now()}`,
        role: "assistant",
        content: "I'd be happy to help you implement this! What CMS is your website built on?",
        cmsOptions: true,
      },
    ]);
    scrollToBottom();
  };

  const handleCmsSelect = (cms: string) => {
    const issue = activeIssue;
    const fixType: IssueFixType = issue ? resolveFixType(issue) : "technical";
    const guide = getCmsImplementationGuide(cms, fixType, issue?.title ?? "this issue");
    const planSteps = issue?.steps?.length
      ? `\n\n**From your action plan:**\n${issue.steps.map((s) => `**Step ${s.step}:** ${s.instruction}`).join("\n\n")}`
      : "";

    setMessages((prev) => [
      ...prev,
      { id: `user-cms-${Date.now()}`, role: "user", content: cms },
      {
        id: `cms-guide-${Date.now()}`,
        role: "assistant",
        content: `${guide}${planSteps}`,
        postFixActions: true,
        cmsGuide: true,
        activeIssueId: issue?.id,
      },
    ]);
    scrollToBottom();
  };

  const handleNewChat = () => {
    if (!domain || !historyReportId || mcpLoading) return;
    if (messages.length > 1) {
      saveChatHistory({ userId: user?.id, cardId, tabId: historyTabId, domain, messages }).catch(() => { });
    }
    startNewChat({ userId: user?.id, cardId, tabId: historyTabId }).catch(() => { });
    chatSessionRef.current = "";
    initWelcome();
    setHasHistory(true);
  };

  const handleLoadHistory = () => {
    if (!domain || !historyReportId || mcpLoading) return;
    loadChatHistory({ userId: user?.id, cardId, tabId: historyTabId }).then((loaded) => {
      if (historyContextRef.current !== historyTabId) return;
      if (loaded?.length) {
        setMessages(loaded as ChatMessage[]);
        setHasHistory(false);
      }
    });
  };

  const handleSendMessage = () => {
    if (!inputValue.trim()) return;
    const text = inputValue.trim();
    setInputValue("");
    setMessages((prev) => [...prev, { id: `user-${Date.now()}`, role: "user", content: text }]);

    if (activeIssue?.needsClarification) {
      generateFix(activeIssue, text);
      return;
    }

    setTimeout(() => {
      setMessages((prev) => [
        ...prev,
        {
          id: `ruby-${Date.now()}`,
          role: "assistant",
          content: "I can help with that. Use **Quick Fixes** to work through your audit issues, or ask me about a specific fix you've already generated.",
          quickFixButton: cardPendingIssues.length > 0,
        },
      ]);
      scrollToBottom();
    }, 600);
    scrollToBottom();
  };

  return (
    <div
      className="relative flex flex-col overflow-hidden rounded-[18px] border border-white/[0.07] bg-og01-card"
      style={{ height: "100%" }}
    >
      {/* Header */}
      <div className="flex flex-shrink-0 items-center gap-3 border-b border-white/[0.07] px-5 py-[18px]">
        <div
          className="grid h-[38px] w-[38px] flex-none place-items-center rounded-[11px]"
          style={{ background: BRAND_GRADIENT, boxShadow: "0 6px 18px -8px rgba(139,92,246,.7)" }}
        >
          <Sparkles className="h-5 w-5 text-white" />
        </div>
        <div>
          <div className="text-[15px] font-semibold text-og01-text">Ask Ruby</div>
          <div className="text-xs text-og01-text-muted">
            {overview?.cards[cardId]?.cardName ?? "AI fix assistant"}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 space-y-3.5 overflow-y-auto px-[18px] py-4">
        {messages.map((msg) => (
          <div key={msg.id}>
            {(msg.content || msg.quickFixButton || msg.isWelcome) && (
              <div className={`flex w-full items-start gap-2.5 ${msg.role === "user" ? "flex-row-reverse" : ""}`}>
                <div
                  className="mt-0.5 grid h-[26px] w-[26px] flex-none place-items-center rounded-lg"
                  style={{ background: msg.role === "assistant" ? BRAND_GRADIENT : "linear-gradient(135deg,#2a2440,#3a2a52)" }}
                >
                  {msg.role === "assistant" ? (
                    <Sparkles className="h-[13px] w-[13px] text-white" />
                  ) : (
                    <User className="h-[13px] w-[13px] text-white" />
                  )}
                </div>
                <div
                  className={`px-[15px] py-3 text-[13.5px] leading-relaxed text-og01-text-secondary ${
                    msg.role === "user" ? "ml-auto max-w-[85%]" : msg.isWelcome ? "min-w-0 flex-1" : "max-w-[85%]"
                  }`}
                  style={{
                    background: "#0F0F18",
                    border: "1px solid rgba(255,255,255,.07)",
                    borderRadius: msg.role === "user" ? "12px 4px 12px 12px" : "4px 12px 12px 12px",
                  }}
                >
                  {msg.isWelcome ? (
                    <RubyWelcomeMessage
                      cardId={cardId}
                      overview={overview}
                      priorityQueue={priorityQueue?.priorityQueue}
                      allIssues={allIssues}
                      mcpLoading={mcpLoading}
                      mcpError={mcpError}
                    />
                  ) : (
                    msg.content && renderContent(msg.content)
                  )}
                </div>
              </div>
            )}

            {cardPendingIssues.length > 0 && (msg.quickFixButton || msg.isWelcome) && (
              <div className="ml-[34px] mt-2.5 flex justify-start">
                <button
                  type="button"
                  onClick={handleQuickFixesClick}
                  className="flex w-full items-center justify-between gap-2.5 rounded-xl px-4 py-3 text-sm font-semibold text-white transition-all hover:brightness-110"
                  style={{ background: BRAND_GRADIENT, boxShadow: "0 8px 24px -12px rgba(255,122,24,.6)" }}
                >
                  <span className="flex items-center gap-2">
                    <Zap className="h-4 w-4" />
                    Quick Fixes — start fixing
                  </span>
                  <ChevronRight className="h-4 w-4 flex-none opacity-90" />
                </button>
              </div>
            )}
            {msg.quickFixList && msg.issues && (
              <QuickFixList
                issues={msg.issues}
                completedIssueIds={completedIds}
                onIssueClick={handleIssueClick}
              />
            )}

            {msg.cmsOptions && (
              <div className="ml-[34px] mt-2 flex flex-wrap gap-2">
                {CMS_OPTIONS.map((cms) => (
                  <button
                    key={cms}
                    type="button"
                    onClick={() => handleCmsSelect(cms)}
                    className="rounded-lg border border-white/[0.07] bg-[#0F0F18] px-3 py-1.5 text-xs font-medium text-og01-text-secondary transition-colors hover:border-white/[0.12] hover:text-og01-text"
                  >
                    {cms}
                  </button>
                ))}
              </div>
            )}

            {msg.codeBlock && (
              <div
                className="ml-[34px] mt-3 overflow-hidden rounded-xl bg-[#0F0F18]"
                style={{ border: `1px solid color-mix(in srgb, ${ACCENT} 26%, transparent)` }}
              >
                <div
                  className="flex items-center justify-between px-4 py-2.5"
                  style={{ background: `color-mix(in srgb, ${ACCENT} 10%, transparent)` }}
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <Code className="h-[15px] w-[15px] shrink-0" style={{ color: ACCENT }} />
                    <span className="truncate text-xs font-semibold text-og01-text">{msg.codeBlock.title}</span>
                    {msg.codeBlock.filename && (
                      <span
                        className="shrink-0 rounded px-1.5 py-0.5 text-[10px] text-og01-text-secondary"
                        style={{ fontFamily: "'Geist Mono', ui-monospace, monospace", background: "rgba(255,255,255,0.06)" }}
                      >
                        {msg.codeBlock.filename}
                      </span>
                    )}
                  </div>
                  <div className="flex shrink-0 gap-1">
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(msg.codeBlock!.code);
                        setCopiedCodeId(msg.id);
                        setTimeout(() => setCopiedCodeId(null), 2000);
                      }}
                      className="flex items-center gap-1 rounded px-2 py-1 text-[10px] text-og01-text-secondary"
                    >
                      {copiedCodeId === msg.id ? <Check className="h-3 w-3" style={{ color: "#22C55E" }} /> : <Copy className="h-3 w-3" />}
                      Copy
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        downloadArtifact(
                          msg.codeBlock!.code,
                          msg.codeBlock!.filename || `${msg.codeBlock!.artifactType}.txt`,
                          msg.codeBlock!.mime || "text/plain"
                        )
                      }
                      className="flex items-center gap-1 rounded px-2 py-1 text-[10px]"
                      style={
                        msg.codeBlock.emphasize
                          ? { color: "#fff", background: ACCENT }
                          : { color: "var(--og01-text-secondary, #9AA0AA)" }
                      }
                      title={`Download ${msg.codeBlock.filename || "file"}`}
                    >
                      <Download className="h-3 w-3" /> Download
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        saveArtifact(
                          {
                            domain,
                            cardId,
                            type: msg.codeBlock!.artifactType as ArtifactType,
                            title: msg.codeBlock!.title,
                            description: `Generated for ${domain}`,
                            code: msg.codeBlock!.code,
                            language: msg.codeBlock!.language,
                            targetUrl: `https://${domain}/`,
                          },
                          user?.id
                        )
                      }
                      className="flex items-center gap-1 rounded px-2 py-1 text-[10px]"
                      style={{ color: "#FF7A18" }}
                    >
                      <Save className="h-3 w-3" /> Save
                    </button>
                  </div>
                </div>
                <pre
                  className="overflow-x-auto px-4 py-3 text-xs text-og01-text-secondary"
                  style={{ fontFamily: "'Geist Mono', ui-monospace, monospace", maxHeight: "300px" }}
                >
                  <code>{msg.codeBlock.code}</code>
                </pre>
              </div>
            )}

            {msg.retryAction && msg.activeIssueId && (
              <div className="ml-[34px] mt-2 flex justify-start">
                <button
                  type="button"
                  onClick={() => handleRetryGeneration(msg.activeIssueId!)}
                  disabled={isLoading}
                  className="flex items-center gap-2 rounded-xl px-4 py-2.5 text-xs font-semibold text-white transition-all hover:brightness-110 disabled:opacity-50"
                  style={{ background: BRAND_GRADIENT, boxShadow: "0 8px 24px -12px rgba(255,122,24,.6)" }}
                >
                  <RotateCcw className="h-4 w-4" />
                  Try again
                </button>
              </div>
            )}

            {msg.postFixActions && msg.activeIssueId && (
              <PostFixActions
                cmsGuide={msg.cmsGuide}
                onFixed={() => handleMarkedFixed(msg.activeIssueId!)}
                onNeedHelp={handleNeedHelp}
              />
            )}
          </div>
        ))}

        {isLoading && (
          <div className="flex items-start gap-2.5">
            <div className="grid h-[26px] w-[26px] place-items-center rounded-lg" style={{ background: BRAND_GRADIENT }}>
              <Sparkles className="h-[13px] w-[13px] text-white" />
            </div>
            <div
              className="flex items-center gap-2 px-[15px] py-3 text-[13.5px] text-og01-text-secondary"
              style={{ background: "#0F0F18", border: "1px solid rgba(255,255,255,.07)", borderRadius: "4px 12px 12px 12px" }}
            >
              <Loader2 className="h-4 w-4 animate-spin" style={{ color: ACCENT }} />
              Ruby is generating your fix...
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Footer mini-actions */}
      <div className="flex flex-shrink-0 gap-2 border-t border-white/[0.07] px-4 py-2.5">
        <button
          type="button"
          onClick={handleLoadHistory}
          disabled={!hasHistory}
          className={`inline-flex items-center gap-1.5 rounded-lg border border-white/[0.07] bg-[#0F0F18] px-[11px] py-1.5 text-xs font-semibold text-og01-text-secondary transition-colors hover:border-white/[0.12] ${hasHistory ? "" : "opacity-40"}`}
        >
          <History className="h-[13px] w-[13px]" />
          Load Chat History
        </button>
        <button
          type="button"
          onClick={handleNewChat}
          className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-white/[0.07] bg-[#0F0F18] px-[11px] py-1.5 text-xs font-semibold text-og01-text-secondary transition-colors hover:border-white/[0.12]"
        >
          <RotateCcw className="h-[13px] w-[13px]" />
          New Chat
        </button>
      </div>

      {/* Composer */}
      <div className="flex-shrink-0 border-t border-white/[0.07] px-4 py-3">
        <div
          className="flex items-center gap-2.5 rounded-xl bg-[#0F0F18] px-3 py-2.5 focus-within:border-[color:var(--accent,#8B5CF6)]"
          style={{ border: "1px solid rgba(255,255,255,.07)" }}
        >
          <input
            type="text"
            placeholder="Ask Ruby anything…"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), handleSendMessage())}
            className="flex-1 bg-transparent text-[13.5px] text-og01-text outline-none placeholder:text-og01-text-muted"
          />
          <button type="button" onClick={() => fileInputRef.current?.click()} className="grid place-items-center text-og01-text-muted hover:text-og01-text-secondary">
            <Paperclip className="h-[17px] w-[17px]" />
          </button>
          <button type="button" onClick={handleSendMessage} className="grid place-items-center" style={{ color: ACCENT }}>
            <Send className="h-[17px] w-[17px]" />
          </button>
        </div>
        <input ref={fileInputRef} type="file" className="hidden" accept="image/*,.pdf,.txt" />
      </div>
    </div>
  );
};

export default RubyQuickFixesWidget;

